import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {ContextualMenuInputsOption} from '@Components/contextual-menu/components/contextual-menu-inputs-option';
import {DEFAULT_QR_MAX_LENGTH, QRType} from '@PosterWhiteboard/items/qr-code-item.class';
import {InputSuggestionsDropdown} from '@Components/input-suggestions-dropdown/input-suggestions-dropdown';
import {useGetLinkInputSuggestions} from '@Hooks/poster-editor/useGetInputSuggestions';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import styles from '@Components/input-suggestions-dropdown/input-suggestions-dropdown.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useAppSelector} from '@/hooks';
import {GA4EventName} from '@Libraries/ga-events';
import type {DropdownIdType, DropdownItemStorage} from '@Components/contextual-menu/components/contextual-menu-dropdown-option';
import {ContextualMenuDropdownOption} from '@Components/contextual-menu/components/contextual-menu-dropdown-option';
import {PhoneNumberInput} from '@Components/phone-number-input';
import {MIN_PHONE_NUM_LENGTH, PhoneNumberInputSize} from '@Components/phone-number-input/phone-number-input.types';
import {isValidEmail} from '@Utils/string.util';
import './qr-content-input-option.scss';

export function QRContentInputOption(): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const {itemMessage, itemQrType} = useAppSelector((state) => {
    const item = state.posterEditor.posterObject?.pages.pagesHashMap[state.posterEditor.currentPageId]?.items.itemsHashMap[activeItemId];
    return {
      itemMessage: item && 'message' in item ? item.message : undefined,
      itemQrType: item && 'qrType' in item && item.qrType ? item.qrType : QRType.LINK,
    };
  });
  const [qrCodeMessage, setQrCodeMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [selectedQrType, setSelectedQrType] = useState<DropdownIdType>(itemQrType);
  const linkSuggestions = useGetLinkInputSuggestions();

  if (!activeItem || (activeItem && !('message' in activeItem)) || itemMessage === undefined) {
    throw new Error(`QR Item doesn't have message property`);
  }

  useEffect((): void => {
    setQrCodeMessage(itemMessage);
  }, [itemMessage]);

  const updateMessage = (message: string): void => {
    void activeItem.updateFromObject({
      message: message,
      qrType: selectedQrType as QRType,
    });
  };

  const onChangeHandler = (updatedValue: string): void => {
    if (!isEditing) {
      setIsEditing(true);
    }
    setQrCodeMessage(updatedValue);
  };

  const onChangeEnded = (updatedValue: string): void => {
    if (updatedValue === '') {
      return;
    }
    if (selectedQrType === QRType.EMAIL.valueOf() && !isValidEmail(updatedValue)) {
      return;
    }
    if (selectedQrType === QRType.PHONE_NUMBER.valueOf() && updatedValue.length < MIN_PHONE_NUM_LENGTH) {
      return;
    }
    if (isEditing) {
      setIsEditing(false);
    }
    updateMessage(updatedValue);
  };

  const getInputValue = (): string => {
    return isEditing ? qrCodeMessage : itemMessage;
  };

  const getInputPlaceholder = (): string => {
    switch (selectedQrType) {
      case QRType.LINK.valueOf():
        return window.i18next.t('pmwjs_add_url');
      case QRType.EMAIL.valueOf():
        return window.i18next.t('pmwjs_add_email_address');
      case QRType.PHONE_NUMBER.valueOf():
      default:
        return '';
    }
  };

  const getInputSelector = (): ReactElement<any> => {
    return (
      <ContextualMenuInputsOption
        id="qr-content-input-selector"
        maxLength={2000}
        errorText={qrCodeMessage.length > DEFAULT_QR_MAX_LENGTH ? window.i18next.t('pmwjs_qr_code_content_limit') : window.i18next.t('pmwjs_valid_email_address')}
        showErrorState={itemMessage.length > DEFAULT_QR_MAX_LENGTH || (!!qrCodeMessage && selectedQrType === QRType.EMAIL.valueOf() && !isValidEmail(qrCodeMessage))}
        onInputChange={onChangeHandler}
        value={getInputValue()}
        onInputChangeEnded={onChangeEnded}
        placeholder={getInputPlaceholder()}
        elementTitle={window.i18next.t('pmwjs_link_placeholder_2')}
      />
    );
  };

  const getDropdownItems = (): DropdownItemStorage[] => {
    return [
      {
        id: QRType.LINK,
        text: window.i18next.t('pmwjs_link'),
      },
      {
        id: QRType.EMAIL,
        text: window.i18next.t('pmwjs_email'),
      },
      {
        id: QRType.PHONE_NUMBER,
        text: window.i18next.t('pmwjs_phone_number'),
      },
    ];
  };

  const onQrTypeUpdated = (itemId: DropdownIdType): void => {
    setSelectedQrType(itemId);
    setQrCodeMessage('');
    setIsEditing(true);
  };

  const getQRTypeText = (): string => {
    switch (selectedQrType) {
      case QRType.LINK.valueOf():
        return window.i18next.t('pmwjs_link');
      case QRType.EMAIL.valueOf():
        return window.i18next.t('pmwjs_email');
      case QRType.PHONE_NUMBER.valueOf():
        return window.i18next.t('pmwjs_phone_number');
    }
    return '';
  };

  const getInputElement = (): ReactElement => {
    switch (selectedQrType) {
      case QRType.EMAIL.valueOf():
        return getInputSelector();
      case QRType.PHONE_NUMBER.valueOf():
        return (
          <PhoneNumberInput
            phoneNumber={getInputValue()}
            onInputChange={onChangeEnded}
            size={PhoneNumberInputSize.MEDIUM}
            className={'react-international-phone-country-selector-custom'}
          />
        );
      case QRType.LINK.valueOf():
      default:
        return (
          <InputSuggestionsDropdown
            dropdownId="qr-content-input-option-dropdown"
            inputSelector={getInputSelector()}
            onSuggestionSelect={(url: string) => {
              onChangeEnded(url);
              window.PMW.gtm.trackGA4CustomEvent(GA4EventName.BUSINESS_PROFILES_DESIGN_EDITOR_QR_CODE);
            }}
            primarySuggestionsList={linkSuggestions}
            popupPosition={DROPDOWN_POSITION.FIXED}
            popupAlignment={ALIGNMENT_TYPE.BOTTOM_END}
            popupClassName={styles.mediumWidth}
            inputValue={getInputValue()}
          />
        );
    }
  };

  return (
    <ContextualMenuDropdownOption
      id={'qr-content-input-option'}
      subOptionHasOverflow={true}
      label={window.i18next.t('pmwjs_qr_content')}
      dropdownSelectorText={getQRTypeText()}
      dropdownItems={getDropdownItems()}
      onClick={onQrTypeUpdated}
      selectedItemId={selectedQrType}
      children={getInputElement()}
    />
  );
}
